import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';

const ManualPaymentsPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [manualPayments, setManualPayments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, approved, rejected
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate(ROUTES.LOGIN);
    } else if (user) {
      loadManualPayments();
      loadUnreadCount();
    }
  }, [user, loading, navigate]);

  const loadUnreadCount = async () => {
    try {
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      const generatedNotifications = [];
      
      const purchases = await apiService.getUserPurchases();
      purchases.forEach((purchase) => {
        const notifId = `purchase-${purchase.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const manualPayments = await apiService.getUserManualPayments();
      manualPayments.forEach((payment) => {
        const notifId = `payment-${payment.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading notification count:', err);
    }
  };

  const loadManualPayments = async () => {
    try {
      setLoadingData(true);
      const payments = await apiService.getUserManualPayments();
      setManualPayments(payments);
    } catch (err) {
      console.error('Error loading manual payments:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-bold">
            <i className="fas fa-clock"></i>
            <span>قيد المراجعة</span>
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-bold">
            <i className="fas fa-check-circle"></i>
            <span>تم القبول</span>
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-bold">
            <i className="fas fa-times-circle"></i>
            <span>مرفوض</span>
          </span>
        );
      default:
        return null;
    }
  };

  const filteredPayments = manualPayments.filter(payment => {
    if (filterStatus === 'all') return true;
    return payment.status === filterStatus;
  });

  if (loading || loadingData) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="relative z-10 py-12 md:py-20 pb-24 md:pb-20 bg-gradient-to-b from-bg to-white min-h-screen">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to={ROUTES.DASHBOARD}
              className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-3 md:mb-4 text-sm md:text-base"
            >
              <i className="fas fa-arrow-right"></i>
              العودة للوحة التحكم
            </Link>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text mb-2">طلبات الدفع اليدوي</h1>
            <p className="text-sm md:text-lg text-text3">تتبع حالة طلبات الدفع اليدوي تاعك</p>
          </div>

          {/* Stats Cards - 4 in one row on all screens */}
          <div className="grid grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
            <div className="bg-white rounded-lg md:rounded-2xl p-2 md:p-6 shadow-custom">
              <div className="flex flex-col items-center md:flex-row md:items-center gap-1 md:gap-3">
                <div className="w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg md:rounded-xl flex items-center justify-center text-white text-xs md:text-base">
                  <i className="fas fa-list"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-primary">{manualPayments.length}</div>
                  <div className="text-[9px] md:text-sm text-text3 leading-tight">إجمالي الطلبات</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-2xl p-2 md:p-6 shadow-custom">
              <div className="flex flex-col items-center md:flex-row md:items-center gap-1 md:gap-3">
                <div className="w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg md:rounded-xl flex items-center justify-center text-white text-xs md:text-base">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-yellow-600">
                    {manualPayments.filter(p => p.status === 'pending').length}
                  </div>
                  <div className="text-[9px] md:text-sm text-text3 leading-tight">قيد المراجعة</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-2xl p-2 md:p-6 shadow-custom">
              <div className="flex flex-col items-center md:flex-row md:items-center gap-1 md:gap-3">
                <div className="w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center text-white text-xs md:text-base">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-green-600">
                    {manualPayments.filter(p => p.status === 'approved').length}
                  </div>
                  <div className="text-[9px] md:text-sm text-text3 leading-tight">تم القبول</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg md:rounded-2xl p-2 md:p-6 shadow-custom">
              <div className="flex flex-col items-center md:flex-row md:items-center gap-1 md:gap-3">
                <div className="w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg md:rounded-xl flex items-center justify-center text-white text-xs md:text-base">
                  <i className="fas fa-times-circle"></i>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-lg md:text-2xl font-black text-red-600">
                    {manualPayments.filter(p => p.status === 'rejected').length}
                  </div>
                  <div className="text-[9px] md:text-sm text-text3 leading-tight">مرفوض</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Buttons - All in one row */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-custom p-3 md:p-4 mb-6 md:mb-8">
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                  filterStatus === 'all'
                    ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-lg'
                    : 'bg-gray-100 text-text3 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-list ml-1 md:ml-2 text-xs md:text-base"></i>
                <span className="hidden sm:inline">الكل</span> ({manualPayments.length})
              </button>
              <button
                onClick={() => setFilterStatus('pending')}
                className={`px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                  filterStatus === 'pending'
                    ? 'bg-gradient-to-br from-yellow-500 to-orange-500 text-white shadow-lg'
                    : 'bg-gray-100 text-text3 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-clock ml-1 md:ml-2 text-xs md:text-base"></i>
                ({manualPayments.filter(p => p.status === 'pending').length})
              </button>
              <button
                onClick={() => setFilterStatus('approved')}
                className={`px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                  filterStatus === 'approved'
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg'
                    : 'bg-gray-100 text-text3 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-check-circle ml-1 md:ml-2 text-xs md:text-base"></i>
                ({manualPayments.filter(p => p.status === 'approved').length})
              </button>
              <button
                onClick={() => setFilterStatus('rejected')}
                className={`px-2 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold transition-all duration-300 text-xs md:text-base ${
                  filterStatus === 'rejected'
                    ? 'bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-lg'
                    : 'bg-gray-100 text-text3 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-times-circle ml-1 md:ml-2 text-xs md:text-base"></i>
                ({manualPayments.filter(p => p.status === 'rejected').length})
              </button>
            </div>
          </div>

          {/* Payment Requests List */}
          {filteredPayments.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-custom">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-inbox text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-black text-text mb-2">لا توجد طلبات</h3>
              <p className="text-text3 mb-6">
                {filterStatus === 'all' 
                  ? 'لم تقم بأي طلبات دفع يدوي حتى الآن'
                  : `لا توجد طلبات ${filterStatus === 'pending' ? 'قيد المراجعة' : filterStatus === 'approved' ? 'مقبولة' : 'مرفوضة'}`
                }
              </p>
              <Link
                to={ROUTES.COURSES}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-primary to-primary-dark text-white rounded-xl font-bold hover:shadow-lg transition-all duration-300"
              >
                <i className="fas fa-graduation-cap"></i>
                <span>تصفح الدورات</span>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="bg-white rounded-2xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Image */}
                    {payment.cover_image && (
                      <div className="flex-shrink-0">
                        <img
                          src={payment.cover_image}
                          alt={payment.course_title}
                          className="w-32 h-32 object-cover rounded-xl"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                        <div>
                          {payment.purchase_type === 'bundle' ? (
                            <>
                              <h3 className="text-xl font-black text-text mb-2 flex items-center gap-2">
                                <i className="fas fa-boxes text-purple-600"></i>
                                حزمة مخصصة ({payment.item_ids ? JSON.parse(payment.item_ids).length : 0} محتويات)
                              </h3>
                              <div className="text-sm text-text3 mb-2">
                                يشمل: {payment.course_title} والمزيد...
                              </div>
                            </>
                          ) : payment.purchase_type === 'subscription' ? (
                            <>
                              <h3 className="text-xl font-black text-text mb-2 flex items-center gap-2">
                                <i className="fas fa-calendar-check text-green-600"></i>
                                الاشتراك الشهري
                              </h3>
                              <div className="text-sm text-text3 mb-2">
                                وصول غير محدود لجميع المحتوى
                              </div>
                            </>
                          ) : (
                            <h3 className="text-xl font-black text-text mb-2">{payment.course_title}</h3>
                          )}
                          <div className="flex flex-wrap gap-2 text-sm text-text3">
                            <span className="flex items-center gap-1">
                              <i className="fas fa-calendar"></i>
                              {new Date(payment.created_at).toLocaleDateString('ar-DZ', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="fas fa-money-bill-wave"></i>
                              {payment.amount} دج
                            </span>
                          </div>
                        </div>
                        {getStatusBadge(payment.status)}
                      </div>

                      {/* Payment Method */}
                      {payment.payment_method && (
                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                          <div className="text-sm font-bold text-text mb-2">
                            <i className="fas fa-credit-card ml-2"></i>
                            طريقة الدفع
                          </div>
                          <div className="text-sm text-text3">{payment.payment_method}</div>
                        </div>
                      )}

                      {/* Transaction ID */}
                      {payment.transaction_id && (
                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                          <div className="text-sm font-bold text-text mb-2">
                            <i className="fas fa-hashtag ml-2"></i>
                            رقم العملية
                          </div>
                          <div className="text-sm text-text3 font-mono">{payment.transaction_id}</div>
                        </div>
                      )}

                      {/* Admin Response */}
                      {payment.admin_response && (
                        <div className={`rounded-xl p-4 ${
                          payment.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                        }`}>
                          <div className="text-sm font-bold text-text mb-2">
                            <i className="fas fa-comment-dots ml-2"></i>
                            رد الإدارة
                          </div>
                          <div className="text-sm text-text2">{payment.admin_response}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ManualPaymentsPage;
